import type { MouseEvent, FocusEvent } from 'react';
import React, { useState, memo, useContext } from 'react';
import type { MutationUpdaterFn } from 'apollo-client';

import {
	TEMPLATE_PREVIEW_EXPERIENCE,
	IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE,
	ExperienceSuccess,
	ExperienceTimeout,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { TemplateCard } from '@confluence/template-card';
import { getTemplateIdAttributesForAnalytics, getTemplateKey } from '@confluence/template-utils';
import { ObservableSpinner } from '@confluence/infinite-scroll';

import type { TemplateListQuery_allTemplates_nodes as Template } from './__types__/TemplateListQuery';
import type { OnTemplateSelect } from './TemplateList';
import { InfiniteScrollWrapper } from './TemplateListStyles';

type TemplateListComponentProps = {
	onTemplateSelect: OnTemplateSelect;
	templates: Template[];
	isDisabled: boolean;
	onStarClick?: MutationUpdaterFn | undefined;
	selectedTemplateKey: string;
	onTemplateMouseEnter?: (template: Template, coordinates: DOMRect, templateIndex: number) => void;
	onTemplateListMouseLeave?: () => void;
	templatesPageSize: number;
};

export const TemplateListComponent = memo(
	({
		onTemplateSelect,
		templates,
		isDisabled,
		onStarClick = () => {},
		onTemplateMouseEnter = () => {},
		onTemplateListMouseLeave = () => {},
		selectedTemplateKey,
		templatesPageSize,
	}: TemplateListComponentProps) => {
		const experienceTracker = useContext(ExperienceTrackerContext);
		const [paginationSlice, setPaginationSlice] = useState<number>(1);

		const renderItemComponent = (template: Template, index: number): JSX.Element => {
			const key = getTemplateKey(template);
			const isActive = selectedTemplateKey === key;
			return (
				<div
					data-testid={key}
					onMouseEnter={getTemplateEventHandler(template, index)}
					onFocus={getTemplateEventHandler(template, index)}
					key={key}
				>
					<TemplateCard<Template>
						template={template}
						onItemClick={handleTemplateClick({ position: index + 1 })}
						isActive={isActive}
						isDisabled={isDisabled}
						onStarClick={onStarClick}
					/>
				</div>
			);
		};

		const getTemplateEventHandler = (template: Template, index: number) => {
			return (e: MouseEvent | FocusEvent) => {
				handleTemplateMouseOverAndFocus(template, e.currentTarget.getBoundingClientRect(), index);
			};
		};

		const handleTemplateMouseOverAndFocus = (
			template: Template,
			element: DOMRect,
			index: number,
		) => {
			if (isDisabled) {
				return;
			}

			experienceTracker.start({
				name: TEMPLATE_PREVIEW_EXPERIENCE,
				timeout: ExperienceTimeout.TEMPLATE_PREVIEW,
				attributes: {
					source: 'templateDrawer',
					...getTemplateIdAttributesForAnalytics(template),
				},
			});

			onTemplateMouseEnter(template, element, index);
		};

		const handleTemplateClick =
			(templateListComponentAdditionalStatAttributes: object) =>
			(template: Template, additionalStatAttributes: object) => {
				onTemplateListMouseLeave();
				onTemplateSelect(template, {
					...templateListComponentAdditionalStatAttributes,
					...additionalStatAttributes,
				});
			};

		const handleNextPage = () => {
			setPaginationSlice(paginationSlice + 1);
		};

		const numRenderedTemplates = templatesPageSize * paginationSlice;
		const hasNextTemplatesPage = numRenderedTemplates < templates.length;

		return (
			<div
				onMouseLeave={onTemplateListMouseLeave}
				onBlur={onTemplateListMouseLeave}
				data-testid="template-list-wrapper"
			>
				{templates.slice(0, numRenderedTemplates).map(renderItemComponent)}
				{hasNextTemplatesPage && (
					<InfiniteScrollWrapper>
						<ObservableSpinner
							cursor={String(paginationSlice)}
							onScrollTo={handleNextPage}
							testId="template-list-observable-spinner"
							observerOptions={{
								root: null,
								rootMargin: '0px',
								threshold: 0.05,
							}}
						/>
					</InfiniteScrollWrapper>
				)}
				<ExperienceSuccess name={IN_EDITOR_TEMPLATES_LOAD_PANEL_TEMPLATE_LIST_EXPERIENCE} />
			</div>
		);
	},
);
